'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { createContext, useContext, useEffect, useState } from 'react'

import {
  LOOP_PARAM_KEYS,
  areLoopParamsExpired,
  areLoopParamsValid,
  clearLoopParams,
  getLoopParams,
  getLoopSession,
  isLoopParam,
  refreshLoopParamsTTL,
  setLoopParams,
  setLoopSession,
} from '../../lib/adapters/loop/loopStorage'

type LoopContextValue = {
  inLoopFlow: boolean
}

const LoopContext = createContext<LoopContextValue>({ inLoopFlow: false })
export const useLoop = () => useContext(LoopContext)

/**
 * Mounts the Loop returns/exchanges entry flow.
 *
 * On page load, detects Loop query params (e.g. `loop_return_id`,
 * `loop_cart_token`), persists them to localStorage, and cleans up the URL.
 *
 * MUST be mounted as a child of `CartProvider`. `useCartStore`'s init effect
 * reads the Loop params/session this provider persists to localStorage, and
 * relies on React flushing child effects before parent effects to see them.
 * Reordering these providers (or making cart init run earlier) breaks the
 * new-loop clear and returning-loop sync with no error — see
 * `LoopProvider.test.tsx`.
 */
export const LoopProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [inLoopFlow, setInLoopFlow] = useState(false)

  // Runs on mount, correcting state for returning users in a Loop flow
  useEffect(() => {
    const params = getLoopParams()
    const areParamsExpired = areLoopParamsExpired()
    if (areLoopParamsValid(params) && !areParamsExpired) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
      setInLoopFlow(true)
    } else if (areParamsExpired) {
      clearLoopParams()
    }
  }, [])

  // Refreshes Loop param TTL on each navigation while the user is in an active Loop flow
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!inLoopFlow) return
    refreshLoopParamsTTL()
  }, [inLoopFlow, pathname])

  // Runs when URL params change, handling fresh entry into a Loop flow
  useEffect(() => {
    if (!pathname || !searchParams) return

    const loopParams: Record<string, string> = {}
    searchParams.forEach((value, key) => {
      if (isLoopParam(key)) {
        loopParams[key] = value
      }
    })

    if (Object.keys(loopParams).length === 0) return
    if (!areLoopParamsValid(loopParams)) return

    setLoopParams(loopParams)
    setInLoopFlow(true)

    // Always write returnId to the session when present. On initial entry,
    // token won't exist yet — it comes from the BE after Create Cart at departure.
    const incomingToken = loopParams[LOOP_PARAM_KEYS.cartToken] ?? null
    const incomingReturnId = loopParams[LOOP_PARAM_KEYS.returnId]
    const existingSession = getLoopSession()
    if (incomingReturnId) {
      const returnIdsMatch =
        !!existingSession?.loop_return_id && existingSession.loop_return_id === incomingReturnId
      // Per Loop's on-store docs, a returning shopper is identified by the return ID
      // against the token *saved at departure* — Loop does not reliably provide `token`
      // in the re-entry URL. An absent incoming token must not demote the flow (or
      // destroy the saved token); only a present-and-different one signals a new cart.
      const isTokenValid =
        !!existingSession?.token && (!incomingToken || existingSession.token === incomingToken)
      const isReturningLoopFlow = returnIdsMatch && isTokenValid

      // Returning flow: existing session matches incoming, flag that we need to sync Loop & Shopify carts
      // New flow: different session values, no sync needed. `token` may not be present but will be issued by BE at checkout.
      setLoopSession({
        token: isReturningLoopFlow ? existingSession!.token : incomingToken,
        loop_return_id: isReturningLoopFlow ? existingSession!.loop_return_id : incomingReturnId,
        syncRequired: isReturningLoopFlow,
        cartInitialized: isReturningLoopFlow ? existingSession.cartInitialized : false,
      })
    }

    const params = new URLSearchParams(searchParams)
    Object.keys(loopParams).forEach(key => params.delete(key))

    router.replace(params.size > 0 ? `${pathname}?${params}` : pathname, { scroll: false })
  }, [pathname, router, searchParams])

  return <LoopContext.Provider value={{ inLoopFlow }}>{children}</LoopContext.Provider>
}
